/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  TextInput,
  Linking,
  Alert,
  FlatList,
  Animated,
  RefreshControl,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
  Feather,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as MailComposer from 'expo-mail-composer';

const LearningHelpSupport = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('faq');
  const [contactMessage, setContactMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    pink: ['#d53f8c', '#fbb6ce'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
  };

  const helpCategories = [
    { id: 'all', label: 'All Topics', icon: 'grid' },
    { id: 'technical', label: 'Technical', icon: 'cog' },
    { id: 'courses', label: 'Courses', icon: 'book-open-variant' },
    { id: 'account', label: 'Account', icon: 'account' },
    { id: 'payment', label: 'Payment', icon: 'credit-card' },
    { id: 'mobile', label: 'Mobile App', icon: 'cellphone' },
  ];

  const faqSections = [
    {
      id: 'getting-started',
      title: '🚀 Getting Started',
      icon: 'rocket',
      color: '#667eea',
      items: [
        {
          id: 'gs1',
          question: 'How do I create an account?',
          answer: 'You can create an account by clicking the "Sign Up" button on the login screen. You can use your email address or sign up with Google. After registration, you\'ll receive a verification email.',
          tags: ['account', 'registration'],
        },
        {
          id: 'gs2',
          question: 'How do I enroll in a course?',
          answer: 'Browse available courses in the Learning Dashboard, tap on any course card, and click the "Enroll" button. For free courses, enrollment is immediate. For paid courses, you\'ll be guided through the payment process.',
          tags: ['courses', 'enrollment'],
        },
        {
          id: 'gs3',
          question: 'Is there a mobile app?',
          answer: 'Yes! You\'re currently using our mobile app. You can also access the platform via web browser at learning.engineeringhub.com. The app provides offline access to downloaded content.',
          tags: ['mobile'],
        },
        {
          id: 'gs4',
          question: 'How do I reset my password?',
          answer: 'On the login screen, tap "Forgot Password?" and enter your email address. You\'ll receive a password reset link. If you don\'t see the email, check your spam folder.',
          tags: ['account', 'security'],
        },
      ],
    },
    {
      id: 'courses-learning',
      title: '📚 Courses & Learning',
      icon: 'book-open',
      color: '#10b981',
      items: [
        {
          id: 'cl1',
          question: 'How do I access course materials?',
          answer: 'Once enrolled, course materials are available in the "My Courses" section. Each course has modules with video lessons, readings, quizzes, and assignments. You can download content for offline access.',
          tags: ['courses', 'materials'],
        },
        {
          id: 'cl2',
          question: 'Are certificates provided?',
          answer: 'Yes! Most courses offer certificates of completion. To earn a certificate, you must complete all required modules and score at least 70% on course assessments. Certificates can be downloaded from your profile.',
          tags: ['courses', 'certificates'],
        },
        {
          id: 'cl3',
          question: 'Can I download videos for offline viewing?',
          answer: 'Yes, most course videos can be downloaded for offline access. Look for the download icon next to video lessons. Note: Live sessions cannot be downloaded but are available for replay for 30 days.',
          tags: ['courses', 'offline'],
        },
        {
          id: 'cl4',
          question: 'How long do I have access to courses?',
          answer: 'You have lifetime access to all courses you enroll in. This includes all future updates to course content. Live session recordings are available for 30 days after the session.',
          tags: ['courses', 'access'],
        },
        {
          id: 'cl5',
          question: 'What if I need help with course content?',
          answer: 'Each course has a discussion forum where you can ask questions. You can also use the AI Assistant for quick help or schedule a one-on-one session with instructors (premium feature).',
          tags: ['courses', 'support'],
        },
      ],
    },
    {
      id: 'technical-issues',
      title: '🔧 Technical Support',
      icon: 'tools',
      color: '#f59e0b',
      items: [
        {
          id: 'ti1',
          question: 'The app is crashing or freezing',
          answer: 'Try these steps:\n1. Close and restart the app\n2. Update to the latest version from App Store/Play Store\n3. Clear app cache in settings\n4. Reinstall the app (your data is saved to the cloud)\n5. Check device storage space',
          tags: ['technical', 'mobile'],
        },
        {
          id: 'ti2',
          question: 'Videos won\'t play or buffer slowly',
          answer: 'Solutions:\n1. Check your internet connection\n2. Try lowering video quality in settings\n3. Download videos for offline viewing\n4. Close other apps using bandwidth\n5. Restart your router',
          tags: ['technical', 'video'],
        },
        {
          id: 'ti3',
          question: 'I can\'t login to my account',
          answer: 'Troubleshooting steps:\n1. Verify your email and password\n2. Reset password if needed\n3. Check internet connection\n4. Try web version at learning.engineeringhub.com\n5. Contact support if issue persists',
          tags: ['technical', 'account'],
        },
        {
          id: 'ti4',
          question: 'Notifications not working',
          answer: 'Ensure:\n1. Notifications are enabled in app settings\n2. Device notifications are enabled\n3. App is not in battery saving mode\n4. You\'re logged into the app\n5. Check notification settings in profile',
          tags: ['technical', 'notifications'],
        },
      ],
    },
    {
      id: 'account-billing',
      title: '💰 Account & Billing',
      icon: 'credit-card',
      color: '#8b5cf6',
      items: [
        {
          id: 'ab1',
          question: 'How do I update my payment method?',
          answer: 'Go to Profile → Settings → Payment Methods. You can add, remove, or update credit cards. All payment information is securely processed through Stripe.',
          tags: ['account', 'payment'],
        },
        {
          id: 'ab2',
          question: 'Can I get a refund?',
          answer: 'We offer a 30-day money-back guarantee for all courses. Contact support within 30 days of purchase for refund requests. Refunds are processed within 5-7 business days.',
          tags: ['payment', 'refund'],
        },
        {
          id: 'ab3',
          question: 'How do I cancel my subscription?',
          answer: 'Go to Profile → Subscriptions → Manage. You can cancel anytime. Cancellation takes effect at the end of your billing period. You\'ll retain access until the period ends.',
          tags: ['account', 'payment'],
        },
        {
          id: 'ab4',
          question: 'Is my payment information secure?',
          answer: 'Yes! We use industry-standard encryption and never store your full payment details on our servers. All payments are processed through PCI-compliant payment gateways.',
          tags: ['payment', 'security'],
        },
      ],
    },
    {
      id: 'features-tools',
      title: '🎨 Features & Tools',
      icon: 'feature-search-outline',
      color: '#ec4899',
      items: [
        {
          id: 'ft1',
          question: 'How does the AI Assistant work?',
          answer: 'The AI Assistant uses machine learning to provide personalized help. It can:\n• Answer course-related questions\n• Recommend learning paths\n• Help with technical problems\n• Provide study tips\n• Career guidance',
          tags: ['features', 'ai'],
        },
        {
          id: 'ft2',
          question: 'What are live sessions?',
          answer: 'Live sessions are interactive webinars with instructors. Features include:\n• Real-time Q&A\n• Whiteboard collaboration\n• Breakout rooms\n• Session recordings\n• Interactive polls',
          tags: ['features', 'live'],
        },
        {
          id: 'ft3',
          question: 'How do study groups work?',
          answer: 'Study groups allow collaborative learning:\n• Join existing groups or create your own\n• Schedule study sessions\n• Share notes and resources\n• Group discussions\n• Peer support',
          tags: ['features', 'community'],
        },
        {
          id: 'ft4',
          question: 'Can I track my learning progress?',
          answer: 'Yes! The Progress Dashboard shows:\n• Course completion percentages\n• Time spent learning\n• Skill development\n• Achievement badges\n• Learning streaks',
          tags: ['features', 'progress'],
        },
      ],
    },
  ];

  const quickGuides = [
    {
      id: 'guide1',
      title: 'Setting Up Your Profile',
      description: 'Complete your profile to get personalized recommendations',
      icon: 'account-cog',
      color: '#667eea',
      steps: [
        'Tap profile icon in top right',
        'Select "Edit Profile"',
        'Add your photo and bio',
        'Set learning preferences',
        'Save changes',
      ],
    },
    {
      id: 'guide2',
      title: 'Downloading Course Content',
      description: 'Learn how to access courses offline',
      icon: 'download',
      color: '#10b981',
      steps: [
        'Go to "My Courses"',
        'Select any course',
        'Tap download icon on videos',
        'Manage downloads in settings',
        'Access offline in "Downloads"',
      ],
    },
    {
      id: 'guide3',
      title: 'Using AI Assistant',
      description: 'Get the most from our AI learning assistant',
      icon: 'robot',
      color: '#f59e0b',
      steps: [
        'Tap AI Assistant button',
        'Ask specific questions',
        'Use for course recommendations',
        'Get study plan suggestions',
        'Request career advice',
      ],
    },
    {
      id: 'guide4',
      title: 'Joining Live Sessions',
      description: 'Participate in interactive live classes',
      icon: 'video',
      color: '#ef4444',
      steps: [
        'Check "Live Sessions" schedule',
        'Register for upcoming sessions',
        'Set calendar reminders',
        'Test audio/video before joining',
        'Participate in Q&A',
      ],
    },
  ];

  const contactOptions = [
    {
      id: 'email',
      title: 'Email Support',
      description: 'Send us an email with your questions',
      icon: 'email',
      color: '#667eea',
      action: () => openEmailSupport(),
    },
    {
      id: 'chat',
      title: 'Live Chat',
      description: 'Chat with our support team (9AM-6PM GMT)',
      icon: 'chat',
      color: '#10b981',
      action: () => openLiveChat(),
    },
    {
      id: 'call',
      title: 'Schedule Call',
      description: 'Book a one-on-one support call',
      icon: 'phone',
      color: '#f59e0b',
      action: () => scheduleCall(),
    },
    {
      id: 'community',
      title: 'Community Forum',
      description: 'Get help from other learners',
      icon: 'forum',
      color: '#8b5cf6',
      action: () => openCommunityForum(),
    },
  ];

  const emergencyResources = [
    {
      id: 'emergency1',
      title: 'Immediate Technical Help',
      description: 'Critical issues affecting your learning',
      icon: 'alert-circle',
      color: '#ef4444',
      items: [
        'Account security concerns',
        'Payment issues',
        'Course access problems',
        'Urgent technical bugs',
      ],
    },
    {
      id: 'emergency2',
      title: 'Learning Support',
      description: 'Quick help for active learners',
      icon: 'lifebuoy',
      color: '#3b82f6',
      items: [
        'Stuck on course material',
        'Need deadline extension',
        'Certificate issues',
        'Grade disputes',
      ],
    },
  ];

  useEffect(() => {
    loadDarkModePreference();
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    // Simulate data refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const openEmailSupport = async () => {
    try {
      const isAvailable = await MailComposer.isAvailableAsync();
      if (isAvailable) {
        await MailComposer.composeAsync({
          recipients: ['support@engineeringlearninghub.com'],
          subject: 'Engineering Learning Hub Support Request',
          body: 'Please describe your issue:\n\n\n\n---\nDevice: ' + Platform.OS + '\nApp Version: 2.1.0',
        });
      } else {
        Linking.openURL('mailto:support@engineeringlearninghub.com');
      }
    } catch (error) {
      console.error('Error opening email:', error);
      Alert.alert('Error', 'Could not open email client. Please email us directly at support@engineeringlearninghub.com');
    }
  };

  const openLiveChat = () => {
    Alert.alert(
      'Live Chat',
      'Live chat is available Monday-Friday, 9AM-6PM GMT.\n\nOur team will respond within 5 minutes during business hours.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Start Chat', onPress: () => navigation.navigate('LiveChatSupport') },
      ]
    );
  };

  const scheduleCall = () => {
    Linking.openURL('https://calendly.com/engineering-learning-support/15min');
  };

  const openCommunityForum = () => {
    Linking.openURL('https://community.engineeringlearninghub.com');
  };

  const submitContactForm = () => {
    if (!contactMessage.trim()) {
      Alert.alert('Error', 'Please enter your message');
      return;
    }

    if (!contactEmail.trim()) {
      Alert.alert('Error', 'Please enter your email address');
      return;
    }

    Alert.alert(
      'Submit Support Request',
      'Are you sure you want to submit this support request?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Submit',
          onPress: async () => {
            try {
              // Here you would typically send to your backend
              console.log('Support request submitted:', {
                email: contactEmail,
                subject: contactSubject,
                message: contactMessage,
              });
              
              Alert.alert(
                'Success',
                'Your support request has been submitted. We\'ll respond within 24 hours.',
                [{ text: 'OK' }]
              );
              
              setContactMessage('');
              setContactEmail('');
              setContactSubject('');
            } catch (error) {
              Alert.alert('Error', 'Failed to submit support request. Please try email instead.');
            }
          },
        },
      ]
    );
  };

  const filteredFAQs = () => {
    let filtered = [];
    
    faqSections.forEach(section => {
      if (selectedCategory === 'all') {
        filtered.push(section);
      } else {
        const filteredItems = section.items.filter(item => 
          item.tags.includes(selectedCategory)
        );
        if (filteredItems.length > 0) {
          filtered.push({
            ...section,
            items: filteredItems,
          });
        }
      }
    });
    
    return filtered;
  };

  const renderHeader = () => (
    <LinearGradient
      colors={gradients.primary}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>🆘 Help & Support</Text>
        <Text style={styles.headerSubtitle}>We're here to help you learn</Text>
      </View>
      <TouchableOpacity 
        style={styles.searchButton}
        onPress={() => setSearchQuery('')}
      >
        <MaterialCommunityIcons name="lifebuoy" size={24} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <MaterialCommunityIcons 
        name="magnify" 
        size={20} 
        color={darkMode ? '#a0aec0' : '#6b7280'} 
        style={styles.searchIcon}
      />
      <TextInput
        style={[styles.searchInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
        placeholder="Search for help topics..."
        placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons 
            name="close-circle" 
            size={20} 
            color={darkMode ? '#a0aec0' : '#6b7280'} 
          />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false}
      style={styles.categoryContainer}
    >
      {helpCategories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryButton,
            selectedCategory === category.id && styles.categoryButtonActive,
            { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#6b7280')} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderTabs = () => (
    <View style={[styles.tabContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <TouchableOpacity
        style={[styles.tabButton, activeTab === 'faq' && styles.tabButtonActive]}
        onPress={() => setActiveTab('faq')}
      >
        <MaterialCommunityIcons 
          name="help-circle" 
          size={20} 
          color={activeTab === 'faq' ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          activeTab === 'faq' && styles.tabTextActive,
        ]}>
          FAQs
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.tabButton, activeTab === 'guides' && styles.tabButtonActive]}
        onPress={() => setActiveTab('guides')}
      >
        <MaterialCommunityIcons 
          name="book-open-page-variant" 
          size={20} 
          color={activeTab === 'guides' ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          activeTab === 'guides' && styles.tabTextActive,
        ]}>
          Guides
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.tabButton, activeTab === 'contact' && styles.tabButtonActive]}
        onPress={() => setActiveTab('contact')}
      >
        <MaterialCommunityIcons 
          name="email" 
          size={20} 
          color={activeTab === 'contact' ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          activeTab === 'contact' && styles.tabTextActive,
        ]}>
          Contact
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.tabButton, activeTab === 'emergency' && styles.tabButtonActive]}
        onPress={() => setActiveTab('emergency')}
      >
        <MaterialCommunityIcons 
          name="alert" 
          size={20} 
          color={activeTab === 'emergency' ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          activeTab === 'emergency' && styles.tabTextActive,
        ]}>
          Emergency
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderFAQSection = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        ❓ Frequently Asked Questions
      </Text>
      
      {filteredFAQs().map((section) => (
        <View 
          key={section.id}
          style={[styles.faqSection, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        >
          <TouchableOpacity
            style={styles.faqSectionHeader}
            onPress={() => toggleSection(section.id)}
          >
            <View style={styles.faqSectionTitleContainer}>
              <View style={[styles.faqIconContainer, { backgroundColor: `${section.color}20` }]}>
                <MaterialCommunityIcons name={section.icon} size={20} color={section.color} />
              </View>
              <Text style={[styles.faqSectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                {section.title}
              </Text>
            </View>
            <MaterialCommunityIcons 
              name={expandedSections[section.id] ? "chevron-up" : "chevron-down"} 
              size={24} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
          
          {expandedSections[section.id] && (
            <View style={styles.faqItemsContainer}>
              {section.items.map((item, index) => (
                <View 
                  key={item.id}
                  style={[
                    styles.faqItem,
                    index < section.items.length - 1 && styles.faqItemBorder,
                  ]}
                >
                  <Text style={[styles.faqQuestion, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {item.question}
                  </Text>
                  <Text style={[styles.faqAnswer, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {item.answer}
                  </Text>
                  <View style={styles.faqTags}>
                    {item.tags.map((tag, tagIndex) => (
                      <View 
                        key={tagIndex}
                        style={[styles.tag, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
                      >
                        <Text style={[styles.tagText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                          {tag}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      ))}
    </Animated.View>
  );

  const renderQuickGuides = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        📖 Quick Guides
      </Text>
      
      <FlatList
        data={quickGuides}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View style={[styles.guideCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
            <LinearGradient
              colors={[item.color, `${item.color}80`]}
              style={styles.guideIconContainer}
            >
              <MaterialCommunityIcons name={item.icon} size={32} color="#ffffff" />
            </LinearGradient>
            
            <Text style={[styles.guideTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              {item.title}
            </Text>
            
            <Text style={[styles.guideDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.description}
            </Text>
            
            <View style={styles.guideSteps}>
              {item.steps.map((step, index) => (
                <View key={index} style={styles.stepRow}>
                  <View style={[styles.stepNumber, { backgroundColor: item.color }]}>
                    <Text style={styles.stepNumberText}>{index + 1}</Text>
                  </View>
                  <Text style={[styles.stepText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {step}
                  </Text>
                </View>
              ))}
            </View>
            
            <TouchableOpacity style={[styles.guideButton, { backgroundColor: item.color }]}>
              <Text style={styles.guideButtonText}>View Detailed Guide</Text>
            </TouchableOpacity>
          </View>
        )}
        contentContainerStyle={styles.guidesList}
      />
    </Animated.View>
  );

  const renderContactOptions = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        📞 Contact Options
      </Text>
      
      <View style={styles.contactOptionsGrid}>
        {contactOptions.map((option) => (
          <TouchableOpacity
            key={option.id}
            style={[styles.contactOptionCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
            onPress={option.action}
          >
            <LinearGradient
              colors={[option.color, `${option.color}80`]}
              style={styles.contactOptionIcon}
            >
              <MaterialCommunityIcons name={option.icon} size={24} color="#ffffff" />
            </LinearGradient>
            
            <Text style={[styles.contactOptionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              {option.title}
            </Text>
            
            <Text style={[styles.contactOptionDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {option.description}
            </Text>
            
            <View style={[styles.contactOptionButton, { backgroundColor: option.color }]}>
              <Text style={styles.contactOptionButtonText}>Contact Now</Text>
              <MaterialCommunityIcons name="chevron-right" size={16} color="#ffffff" />
            </View>
          </TouchableOpacity>
        ))}
      </View>
      
      <Text style={[styles.sectionSubtitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        Or send us a message directly:
      </Text>
      
      <View style={[styles.contactForm, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        <TextInput
          style={[styles.contactInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Your email address"
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={contactEmail}
          onChangeText={setContactEmail}
        />
        
        <TextInput
          style={[styles.contactInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Subject (optional)"
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={contactSubject}
          onChangeText={setContactSubject}
        />
        
        <TextInput
          style={[styles.contactTextArea, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Describe your issue or question..."
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={contactMessage}
          onChangeText={setContactMessage}
          multiline
          numberOfLines={6}
          textAlignVertical="top"
        />
        
        <TouchableOpacity 
          style={styles.submitButton}
          onPress={submitContactForm}
        >
          <LinearGradient
            colors={gradients.primary}
            style={styles.submitButtonGradient}
          >
            <MaterialCommunityIcons name="send" size={20} color="#ffffff" />
            <Text style={styles.submitButtonText}>Send Message</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderEmergencyResources = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        🚨 Emergency Resources
      </Text>
      
      {emergencyResources.map((resource) => (
        <View 
          key={resource.id}
          style={[styles.emergencyCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        >
          <View style={styles.emergencyHeader}>
            <LinearGradient
              colors={[resource.color, `${resource.color}80`]}
              style={styles.emergencyIcon}
            >
              <MaterialCommunityIcons name={resource.icon} size={24} color="#ffffff" />
            </LinearGradient>
            <View style={styles.emergencyTitleContainer}>
              <Text style={[styles.emergencyTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                {resource.title}
              </Text>
              <Text style={[styles.emergencyDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {resource.description}
              </Text>
            </View>
          </View>
          
          <View style={styles.emergencyItems}>
            {resource.items.map((item, index) => (
              <View key={index} style={styles.emergencyItem}>
                <MaterialCommunityIcons 
                  name="checkbox-marked-circle" 
                  size={16} 
                  color={resource.color} 
                />
                <Text style={[styles.emergencyItemText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {item}
                </Text>
              </View>
            ))}
          </View>
          
          <TouchableOpacity 
            style={[styles.emergencyButton, { backgroundColor: resource.color }]}
            onPress={openEmailSupport}
          >
            <MaterialCommunityIcons name="message-alert" size={20} color="#ffffff" />
            <Text style={styles.emergencyButtonText}>Get Immediate Help</Text>
          </TouchableOpacity>
        </View>
      ))}
      
      <View style={[styles.emergencyContact, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        <Text style={[styles.emergencyContactTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
          📞 Immediate Phone Support
        </Text>
        <Text style={[styles.emergencyContactNumber, { color: '#ef4444' }]}>
          +44 (0) 20 7123 4567
        </Text>
        <Text style={[styles.emergencyContactHours, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Available 24/7 for emergency technical issues
        </Text>
      </View>
    </Animated.View>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'faq':
        return renderFAQSection();
      case 'guides':
        return renderQuickGuides();
      case 'contact':
        return renderContactOptions();
      case 'emergency':
        return renderEmergencyResources();
      default:
        return renderFAQSection();
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.scrollView}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {renderSearchBar()}
        {renderCategoryFilter()}
        {renderTabs()}
        
        {renderActiveTab()}
        
        <View style={styles.bottomSection}>
          <Text style={[styles.bottomTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            Still need help?
          </Text>
          <Text style={[styles.bottomText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Our support team typically responds within 24 hours. For urgent matters, use the emergency contact options.
          </Text>
          
          <View style={styles.bottomStats}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="clock" size={20} color="#667eea" />
              <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>24h</Text>
              <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Avg Response</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
              <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>98%</Text>
              <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Satisfaction</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="account-group" size={20} color="#8b5cf6" />
              <Text style={[styles.statNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>50+</Text>
              <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>Experts</Text>
            </View>
          </View>
        </View>
        
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  searchButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginVertical: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
  categoryContainer: {
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  categoryButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  categoryText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
  },
  categoryTextActive: {
    color: '#ffffff',
  },
  tabContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tabButton: {
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  tabButtonActive: {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  tabText: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '500',
  },
  tabTextActive: {
    color: '#667eea',
    fontWeight: '600',
  },
  section: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  sectionSubtitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  faqSection: {
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  faqSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  faqSectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  faqIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  faqSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  faqItemsContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  faqItem: {
    paddingVertical: 12,
  },
  faqItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  faqQuestion: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 8,
    lineHeight: 22,
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  faqTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  tagText: {
    fontSize: 12,
  },
  guidesList: {
    paddingVertical: 8,
  },
  guideCard: {
    width: 280,
    borderRadius: 16,
    padding: 16,
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  guideIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  guideTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  guideDescription: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  guideSteps: {
    marginBottom: 16,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  stepNumberText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  stepText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  guideButton: {
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  guideButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  contactOptionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  contactOptionCard: {
    width: '48%',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  contactOptionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  contactOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  contactOptionDescription: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 16,
  },
  contactOptionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  contactOptionButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginRight: 4,
  },
  contactForm: {
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  contactInput: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 12,
  },
  contactTextArea: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 16,
    minHeight: 120,
  },
  submitButton: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  submitButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  emergencyCard: {
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  emergencyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  emergencyIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  emergencyTitleContainer: {
    flex: 1,
  },
  emergencyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  emergencyDescription: {
    fontSize: 14,
  },
  emergencyItems: {
    marginBottom: 16,
  },
  emergencyItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  emergencyItemText: {
    flex: 1,
    fontSize: 14,
    marginLeft: 12,
    lineHeight: 20,
  },
  emergencyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
  },
  emergencyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  emergencyContact: {
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  emergencyContactTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  emergencyContactNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  emergencyContactHours: {
    fontSize: 14,
    textAlign: 'center',
  },
  bottomSection: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginTop: 16,
  },
  bottomTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  bottomText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  bottomStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginVertical: 8,
  },
  statLabel: {
    fontSize: 12,
  },
  bottomPadding: {
    height: 40,
  },
});

export default LearningHelpSupport;